import { useState } from 'react';
import { CalendarIcon, Info } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Calendar } from '@/components/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { formatDateRangeLabel, isoToLocalDate, localDateToIso } from '@/utils/dateRangePicker';
import {
  dateRangesEqual,
  resolveWorkspaceDatePreset,
} from '@/api/reportDateRanges';
import {
  WORKSPACE_DATE_PRESETS,
  type WorkspaceDatePresetId,
} from '@/constants/Constant_Preset_Variables';

const DASHBOARD_RANGE_HELP =
  'Tables show campaigns whose schedule overlaps this range, and related keywords. Performance metrics (clicks, spend, sales, ROAS, etc.) use reporting data for the same date range.';

function appliedToDraft(startDate: string, endDate: string): DateRange | undefined {
  const from = isoToLocalDate(startDate);
  const to = isoToLocalDate(endDate);
  if (!from) return undefined;
  return { from, to: to ?? from };
}

export function DashboardDateRangePicker({
  startDate,
  endDate,
  onChange,
  invalidRange,
  disabled,
  variant = 'chrome',
  presets = WORKSPACE_DATE_PRESETS,
  activePresetId,
  onPresetSelect,
  showHelp = true,
  fromDate,
  toDate,
}: {
  startDate: string;
  endDate: string;
  onChange: (range: { startDate: string; endDate: string }) => void;
  invalidRange?: boolean;
  disabled?: boolean;
  variant?: 'chrome' | 'default';
  presets?: ReadonlyArray<{ id: WorkspaceDatePresetId; label: string }>;
  activePresetId?: WorkspaceDatePresetId;
  onPresetSelect?: (id: WorkspaceDatePresetId) => void;
  showHelp?: boolean;
  fromDate?: Date;
  toDate?: Date;
}) {
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftRange(appliedToDraft(startDate, endDate));
    }
    setOpen(nextOpen);
  };

  const draftStart = draftRange?.from ? localDateToIso(draftRange.from) : '';
  const draftEnd = draftRange?.to
    ? localDateToIso(draftRange.to)
    : draftRange?.from
      ? localDateToIso(draftRange.from)
      : '';
  const isDraftInvalid = Boolean(draftStart && draftEnd && draftEnd < draftStart);
  const canApply = Boolean(draftRange?.from && draftRange?.to && !isDraftInvalid);
  const activePreset = activePresetId ?? presets.find((preset) =>
    dateRangesEqual(
      { startDate, endDate },
      resolveWorkspaceDatePreset(preset.id).current
    )
  )?.id;

  const handleApply = () => {
    if (!canApply || !draftRange?.from || !draftRange?.to) return;
    onChange({
      startDate: localDateToIso(draftRange.from),
      endDate: localDateToIso(draftRange.to),
    });
    setOpen(false);
  };

  const handlePresetSelect = (presetId: WorkspaceDatePresetId) => {
    if (onPresetSelect) {
      onPresetSelect(presetId);
    } else {
      onChange(resolveWorkspaceDatePreset(presetId).current);
    }
    setOpen(false);
  };

  const isChrome = variant === 'chrome';
  const hasPresets = presets.length > 0;

  return (
    <div className="flex items-center gap-1.5">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'inline-flex h-[34px] items-center gap-2 rounded-lg border px-3 text-body-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              isChrome
                ? 'border-chrome-border bg-chrome/50 text-chrome-text hover:bg-white/10'
                : 'border-border bg-surface text-foreground shadow-sm hover:bg-surface-muted',
              invalidRange && 'border-error text-error'
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
            <span className="max-w-[min(240px,42vw)] truncate">
              {formatDateRangeLabel(startDate, endDate)}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            {hasPresets ? (
              <div className="flex w-40 shrink-0 flex-col gap-0.5 border-r border-border bg-surface-muted/50 p-2">
                {presets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handlePresetSelect(preset.id)}
                    className={cn(
                      'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      activePreset === preset.id
                        ? 'bg-brand/10 font-semibold text-brand'
                        : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            ) : null}
            <div>
              <Calendar
                mode="range"
                defaultMonth={isoToLocalDate(endDate) ?? isoToLocalDate(startDate)}
                selected={draftRange}
                onSelect={setDraftRange}
                numberOfMonths={2}
                endMonth={toDate}
                disabled={[
                  ...(fromDate ? [{ before: fromDate }] : []),
                  ...(toDate ? [{ after: toDate }] : []),
                ]}
              />
              <div className="flex flex-col gap-2 border-t border-border px-3 py-3">
                {isDraftInvalid ? (
                  <p className="text-body-sm text-error">
                    End date must be on or after the start date.
                  </p>
                ) : !draftRange?.to ? (
                  <p className="text-body-sm text-foreground-muted">Select a start and end date.</p>
                ) : null}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="button" size="sm" onClick={handleApply} disabled={!canApply}>
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {showHelp ? (
        <button
          type="button"
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-md p-1 transition-colors',
            isChrome
              ? 'text-chrome-muted hover:bg-white/10 hover:text-chrome-text'
              : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground'
          )}
          title={DASHBOARD_RANGE_HELP}
          aria-label="Time range help"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
